// PublishDocumentScreen.web.js
import React, { useEffect, useState } from "react";
import {
  ActivityIndicator,
  Image,
  SafeAreaView,
  ScrollView,
  StatusBar,
  StyleSheet,
  Text,
  TextInput,
  TouchableOpacity,
  View,
} from "react-native";
import { MaterialCommunityIcons } from "@expo/vector-icons";
import { LinearGradient } from "expo-linear-gradient";
import * as ImagePicker from "expo-image-picker";
import * as DocumentPicker from "expo-document-picker";
import { useTheme } from "../screens/ThemeContext";
import { getStorage, ref, uploadBytesResumable, getDownloadURL } from "firebase/storage";
import { collection, addDoc, serverTimestamp } from "firebase/firestore";
import { db } from "../private/firebaseConfig";
import AdminLayout from "./web-ui/AdminLayout";

const convertFirebaseURL = (url) => {
  if (!url) return null;

  // Convertir .firebasestorage.app en .appspot.com
  return url.replace(
    /firebasestorage\.googleapis\.com\/v0\/b\/([^/]+)\.firebasestorage\.app/,
    'firebasestorage.googleapis.com/v0/b/$1.appspot.com'
  );
};

// ⚠️ Fonctionnalité "restriction de téléchargement" SUSPENDUE.
// Passer à true pour réafficher l'option dans le formulaire de publication.
const RESTRICTION_FEATURE_ENABLED = false;

const documentTypes = [
  {
    id: "concours",
    title: "Sujets de Concours",
    icon: "file-document-multiple",
    description: "Publiez des sujets de concours",
    gradient: ["#4C6FFF", "#6B8CFF"],
  },
  {
    id: "bords",
    title: "Bords",
    icon: "book-open-variant",
    description: "Publiez des bords de préparation",
    gradient: ["#FF4C4C", "#FF6B6B"],
  },
  {
    id: "simple",
    title: "Document Simple",
    icon: "file",
    description: "Publiez un document simple",
    gradient: ["#4CAF50", "#6BBF6E"],
  },
];

// ---------- helpers de réordonnancement (↑/↓) ----------
const moveItem = (arr, from, to) => {
  if (to < 0 || to >= arr.length) return arr;
  const copy = [...arr];
  const [it] = copy.splice(from, 1);
  copy.splice(to, 0, it);
  return copy;
};

// Composant RichTextEditor défini en dehors pour éviter les re-renders
const RichTextEditor = ({ value, onChange, placeholder, style, theme }) => {
  const [selection, setSelection] = useState({ start: 0, end: 0 });

  const applyFormat = (format) => {
    const { start, end } = selection;
    const selectedText = value.substring(start, end);
    let formattedText = "";

    switch (format) {
      case "bold":
        formattedText = `**${selectedText}**`;
        break;
      case "italic":
        formattedText = `*${selectedText}*`;
        break;
      case "underline":
        formattedText = `__${selectedText}__`;
        break;
      case "list":
        formattedText = `- ${selectedText}`;
        break;
      default:
        return;
    }

    const newValue = value.substring(0, start) + formattedText + value.substring(end);
    onChange(newValue);
  };

  return (
    <View style={style}>
      {/* Barre d'outils de formatage toujours visible */}
      <View style={[styles.formattingToolbar, { backgroundColor: theme.surface, borderColor: theme.border }]}>
        <TouchableOpacity style={styles.formatButton} onPress={() => applyFormat("bold")}>
          <MaterialCommunityIcons name="format-bold" size={16} color={theme.text} />
        </TouchableOpacity>
        <TouchableOpacity style={styles.formatButton} onPress={() => applyFormat("italic")}>
          <MaterialCommunityIcons name="format-italic" size={16} color={theme.text} />
        </TouchableOpacity>
        <TouchableOpacity style={styles.formatButton} onPress={() => applyFormat("underline")}>
          <MaterialCommunityIcons name="format-underline" size={16} color={theme.text} />
        </TouchableOpacity>
        <TouchableOpacity style={styles.formatButton} onPress={() => applyFormat("list")}>
          <MaterialCommunityIcons name="format-list-bulleted" size={16} color={theme.text} />
        </TouchableOpacity>
      </View>
      
      <TextInput
        style={[
          styles.input,
          styles.textArea,
          { backgroundColor: theme.background, color: theme.text, borderColor: theme.border },
        ]}
        placeholder={placeholder}
        value={value}
        onChangeText={onChange}
        onSelectionChange={(event) => {
          setSelection({
            start: event.nativeEvent.selection.start,
            end: event.nativeEvent.selection.end,
          });
        }}
        multiline
        numberOfLines={4}
        placeholderTextColor={theme.textSecondary}
      />
      <Text style={[styles.formattingHint, { color: theme.textSecondary }]}>
        Sélectionnez du texte puis cliquez sur un bouton de formatage
      </Text>
    </View>
  );
};

export default function PublishDocumentScreen({ navigation }) {
  const { theme: baseTheme } = useTheme();
  // Sur le web (dashboard), on surcharge le theme pour matcher le style admin
  // (fond clair, cartes blanches, bordures fines, accent brand).
  const isWeb = typeof window !== 'undefined' && !!window.document;
  const theme = isWeb
    ? {
        ...baseTheme,
        background: '#f7f9fc',
        surface: '#ffffff',
        border: '#e4e8ef',
        primary: '#165ca2',
        primaryDark: '#0e4079',
        text: '#1a2236',
        textSecondary: '#5d6781',
        success: '#10b981',
        danger: '#ef4444',
      }
    : baseTheme;
  const [selectedType, setSelectedType] = useState(null);
  const [loading, setLoading] = useState(false);
  const [uploadProgress, setUploadProgress] = useState(0);
  const [formValid, setFormValid] = useState(false);

  // Concours
  const [concoursData, setConcoursData] = useState({
    sigle: "",
    nomComplet: "",
    description: "",
    niveau: "",
    langue: "fr",
    typeConcours: "",
    hasGroups: false,
    groups: [],
    arretes: [],
    documents: [],
    coverImage: null,
    restrictionTelechargement: false,
  });

  // Bords
  const [bordsData, setBordsData] = useState({
    titre: "",
    nomDocument: "",
    description: "",
    image: null,
    documents: [],
    documentsAnglais: [], // Version anglaise du bord
    documentsOptionnels: [], // Nouveau: documents optionnels
    liens: [], // Nouveau: liens clickables
    prix: "",
    prixInitial: "",
    reductionPourcent: "0",
    prixFinal: "",
    nombrePages: "",
    echantillons: [],
    langue: "fr",
    feuilletage: null,
    restrictionTelechargement: false,
  });

  // Simple
  const [simpleData, setSimpleData] = useState({
    titre: "",
    nomDocument: "",
    description: "",
    document: null,
    coverImage: null,
    liens: [],
    restrictionTelechargement: false, // Nouveau: liens clickables
  });

  useEffect(() => {
    if (!selectedType) return;
    let ok = false;
    if (selectedType === "concours") ok = validateConcoursForm();
    if (selectedType === "bords") ok = validateBordsForm();
    if (selectedType === "simple") ok = validateSimpleForm();
    setFormValid(ok);
  }, [selectedType, concoursData, bordsData, simpleData]);

  // Fonction pour calculer la réduction
  const calculateReduction = (prix, reductionPourcent) => {
    const prixNum = parseFloat(prix) || 0;
    const reductionNum = parseFloat(reductionPourcent) || 0;

    if (prixNum <= 0 || reductionNum <= 0 || reductionNum > 100) {
      return {
        prixInitial: prix,
        reductionPourcent: '0',
        prixFinal: prix
      };
    }

    const montantReduction = (prixNum * reductionNum) / 100;
    const prixFinal = prixNum - montantReduction;

    return {
      prixInitial: prix,
      reductionPourcent: reductionPourcent,
      prixFinal: prixFinal.toString()
    };
  };

  // Mise à jour du prix avec réduction pour bords
  const handleBordsPrixChange = (text) => {
    const result = calculateReduction(text, bordsData.reductionPourcent);
    setBordsData(prev => ({
      ...prev,
      prix: text,
      prixInitial: result.prixInitial,
      prixFinal: result.prixFinal,
      reductionPourcent: result.reductionPourcent
    }));
  };

  const handleBordsReductionChange = (text) => {
    const reductionNum = parseFloat(text) || 0;
    if (reductionNum > 100) return;

    const result = calculateReduction(bordsData.prix, text);
    setBordsData(prev => ({
      ...prev,
      reductionPourcent: text,
      prixInitial: result.prixInitial,
      prixFinal: result.prixFinal
    }));
  };

  // Fonctions pour gérer la réduction des documents concours (avec groupes)
  const handleConcoursPrixChange = (groupId, docIndex, text) => {
    setConcoursData((prev) => ({
      ...prev,
      groups: prev.groups.map((group) =>
        group.id === groupId
          ? {
              ...group,
              documents: group.documents.map((doc, index) => {
                if (index === docIndex) {
                  const result = calculateReduction(text, doc.reductionPourcent || '0');
                  return {
                    ...doc,
                    prix: text,
                    prixInitial: result.prixInitial,
                    prixFinal: result.prixFinal
                  };
                }
                return doc;
              })
            }
          : group
      )
    }));
  };

  const handleConcoursReductionChange = (groupId, docIndex, text) => {
    const reductionNum = parseFloat(text) || 0;
    if (reductionNum > 100) return;

    setConcoursData((prev) => ({
      ...prev,
      groups: prev.groups.map((group) =>
        group.id === groupId
          ? {
              ...group,
              documents: group.documents.map((doc, index) => {
                if (index === docIndex) {
                  const result = calculateReduction(doc.prix, text);
                  return {
                    ...doc,
                    reductionPourcent: text,
                    prixInitial: result.prixInitial,
                    prixFinal: result.prixFinal
                  };
                }
                return doc;
              })
            }
          : group
      )
    }));
  };

  // Pour les documents sans groupes
  const handleConcoursDocPrixChange = (docIndex, text) => {
    setConcoursData((prev) => ({
      ...prev,
      documents: prev.documents.map((doc, index) => {
        if (index === docIndex) {
          const result = calculateReduction(text, doc.reductionPourcent || '0');
          return {
            ...doc,
            prix: text,
            prixInitial: result.prixInitial,
            prixFinal: result.prixFinal
          };
        }
        return doc;
      })
    }));
  };

  const handleConcoursDocReductionChange = (docIndex, text) => {
    const reductionNum = parseFloat(text) || 0;
    if (reductionNum > 100) return;

    setConcoursData((prev) => ({
      ...prev,
      documents: prev.documents.map((doc, index) => {
        if (index === docIndex) {
          const result = calculateReduction(doc.prix, text);
          return {
            ...doc,
            reductionPourcent: text,
            prixInitial: result.prixInitial,
            prixFinal: result.prixFinal
          };
        }
        return doc;
      })
    }));
  };

  // ---------- validations ----------
  const validateConcoursForm = () => {
    const { sigle, nomComplet, description, niveau, langue, typeConcours, hasGroups, groups, documents, arretes } =
      concoursData;

    const arretesValid = arretes.every(
      (a) => a.titre?.trim() && a.annee?.toString().trim() && (a.document || a.url?.trim())
    );

    const docsValid = hasGroups
      ? groups.every(
          (g) =>
            g.nom?.trim() &&
            g.documents.every(
              (d) =>
                d.titre?.trim() &&
                d.filiere?.trim() &&
                d.annee?.toString().trim() &&
                d.nombrePages?.toString().trim() &&
                d.prix?.toString().trim() &&
                d.document
            )
        )
      : documents.every(
          (d) =>
            d.titre?.trim() &&
            d.filiere?.trim() &&
            d.annee?.toString().trim() &&
            d.nombrePages?.toString().trim() &&
            d.prix?.toString().trim() &&
            d.document
        );

    const hasDocs = hasGroups ? groups.some((g) => g.documents.length > 0) : documents.length > 0;

    return Boolean(
      sigle?.trim() &&
        nomComplet?.trim() &&
        description?.trim() &&
        niveau?.trim() &&
        langue &&
        typeConcours?.trim() &&
        hasDocs &&
        docsValid &&
        arretesValid
    );
  };

  const validateBordsForm = () =>
    Boolean(
      bordsData.titre?.trim() &&
        bordsData.nomDocument?.trim() &&
        bordsData.description?.trim() &&
        bordsData.image &&
        bordsData.documents.length > 0 &&
        bordsData.prix?.trim() &&
        bordsData.nombrePages?.trim() &&
        bordsData.echantillons.length > 0 &&
        bordsData.langue
    );

  const validateSimpleForm = () =>
    Boolean(simpleData.titre?.trim() && simpleData.nomDocument?.trim() && simpleData.description?.trim() && simpleData.document);

  // ---------- pickers ----------
  const pickImage = async (target, isEchantillon = false) => {
    const result = await ImagePicker.launchImageLibraryAsync({
      mediaTypes: ImagePicker.MediaTypeOptions.Images,
      allowsEditing: true,
      quality: 0.85,
    });
    if (result.canceled) return;
    const uri = result.assets[0].uri;
    if (target === "concours") setConcoursData((p) => ({ ...p, coverImage: uri }));
    if (target === "simple") setSimpleData((p) => ({ ...p, coverImage: uri }));
    if (target === "bords") {
      if (isEchantillon) setBordsData((p) => ({ ...p, echantillons: [...p.echantillons, uri] }));
      else setBordsData((p) => ({ ...p, image: uri }));
    }
  };

  const pickPDF = async () => {
    const res = await DocumentPicker.getDocumentAsync({
      type: "application/pdf",
      multiple: false,
      copyToCacheDirectory: true,
    });
    if (res.canceled || !res.assets?.[0]) return;
    const file = res.assets[0];

    if (selectedType === "concours") {
      setConcoursData((p) => ({
        ...p,
        // Ajout EN HAUT de la liste (pas à la fin) — section sans groupe
        documents: [
          {
            id: Date.now().toString(36),
            document: file,
            titre: "",
            filiere: "",
            annee: new Date().getFullYear(),
            nombrePages: "",
            prix: "",
            prixInitial: "",
            reductionPourcent: "0",
            prixFinal: "",
            gratuit: false,
          },
          ...p.documents,
        ],
      }));
    } else if (selectedType === "bords") {
      setBordsData((p) => ({ ...p, documents: [...p.documents, file] }));
    } else if (selectedType === "simple") {
      setSimpleData((p) => ({ ...p, document: file }));
    }
  };



  const pickArretePDF = async (arreteId) => {
    const res = await DocumentPicker.getDocumentAsync({
      type: "application/pdf",
      multiple: false,
      copyToCacheDirectory: true,
    });
    if (res.canceled || !res.assets?.[0]) return;
    const file = res.assets[0];
    setConcoursData((p) => ({
      ...p,
      arretes: p.arretes.map((a) => (a.id === arreteId ? { ...a, document: file } : a)),
    }));
  };

  const pickFeuilletagePDF = async () => {
    const res = await DocumentPicker.getDocumentAsync({
      type: "application/pdf",
      multiple: false,
      copyToCacheDirectory: true,
    });
    if (res.canceled || !res.assets?.[0]) return;
    setBordsData((p) => ({ ...p, feuilletage: res.assets[0] }));
  };

  // Nouveau: Ajouter un document optionnel pour les bords
  const pickDocumentOptionnel = async () => {
    const res = await DocumentPicker.getDocumentAsync({
      type: "application/pdf",
      multiple: false,
      copyToCacheDirectory: true,
    });
    if (res.canceled || !res.assets?.[0]) return;
    const file = res.assets[0];
    setBordsData((p) => ({
      ...p,
      // Ajout EN HAUT de la liste — demande utilisateur
      documentsOptionnels: [
        {
          id: Date.now().toString(36),
          document: file,
          titre: "",
          description: "",
          prix: "", // Optionnel : vide = document inclus / gratuit
        },
        ...p.documentsOptionnels,
      ],
    }));
  };

  // Nouveau: Supprimer un document optionnel
  const removeDocumentOptionnel = (id) => {
    setBordsData((p) => ({
      ...p,
      documentsOptionnels: p.documentsOptionnels.filter((d) => d.id !== id),
    }));
  };

  // Nouveau: Mettre à jour un document optionnel
  const updateDocumentOptionnel = (id, field, value) => {
    setBordsData((p) => ({
      ...p,
      documentsOptionnels: p.documentsOptionnels.map((d) =>
        d.id === id ? { ...d, [field]: value } : d
      ),
    }));
  };

  // Nouveau: Ajouter un lien
  const addLien = (type) => {
    if (type === "bords") {
      setBordsData((p) => ({
        ...p,
        liens: [
          ...p.liens,
          {
            id: Date.now().toString(36),
            titre: "",
            url: "",
            description: "",
          },
        ],
      }));
    } else if (type === "simple") {
      setSimpleData((p) => ({
        ...p,
        liens: [
          ...p.liens,
          {
            id: Date.now().toString(36),
            titre: "",
            url: "",
            description: "",
          },
        ],
      }));
    }
  };

  // Nouveau: Supprimer un lien
  const removeLien = (type, id) => {
    if (type === "bords") {
      setBordsData((p) => ({
        ...p,
        liens: p.liens.filter((l) => l.id !== id),
      }));
    } else if (type === "simple") {
      setSimpleData((p) => ({
        ...p,
        liens: p.liens.filter((l) => l.id !== id),
      }));
    }
  };

  // Nouveau: Mettre à jour un lien
  const updateLien = (type, id, field, value) => {
    if (type === "bords") {
      setBordsData((p) => ({
        ...p,
        liens: p.liens.map((l) => (l.id === id ? { ...l, [field]: value } : l)),
      }));
    } else if (type === "simple") {
      setSimpleData((p) => ({
        ...p,
        liens: p.liens.map((l) => (l.id === id ? { ...l, [field]: value } : l)),
      }));
    }
  };

  // ---------- CRUD local ----------
  const addArrete = () =>
    setConcoursData((p) => ({
      ...p,
      arretes: [
        ...p.arretes,
        { id: crypto.randomUUID?.() || Date.now().toString(36), titre: "", annee: new Date().getFullYear(), document: null, url: "" },
      ],
    }));

  const removeArrete = (id) =>
    setConcoursData((p) => ({ ...p, arretes: p.arretes.filter((a) => a.id !== id) }));

  const updateArrete = (id, field, value) =>
    setConcoursData((p) => ({
      ...p,
      arretes: p.arretes.map((a) => (a.id === id ? { ...a, [field]: value } : a)),
    }));

  const addGroup = () =>
    setConcoursData((p) => ({
      ...p,
      groups: [...p.groups, { id: crypto.randomUUID?.() || Date.now().toString(36), nom: "", documents: [] }],
    }));

  const removeGroup = (id) =>
    setConcoursData((p) => ({ ...p, groups: p.groups.filter((g) => g.id !== id) }));

  const updateGroup = (id, field, value) =>
    setConcoursData((p) => ({
      ...p,
      groups: p.groups.map((g) => (g.id === id ? { ...g, [field]: value } : g)),
    }));

  const addDocumentToGroup = async (groupId) => {
    const res = await DocumentPicker.getDocumentAsync({
      type: "application/pdf",
      multiple: false,
      copyToCacheDirectory: true,
    });
    if (res.canceled || !res.assets?.[0]) return;
    const file = res.assets[0];
    setConcoursData((p) => ({
      ...p,
      groups: p.groups.map((g) =>
        g.id === groupId
          ? {
              ...g,
              // Ajout EN HAUT du groupe — demande utilisateur
              documents: [
                {
                  id: crypto.randomUUID?.() || Date.now().toString(36),
                  document: file,
                  titre: "",
                  filiere: "",
                  annee: new Date().getFullYear(),
                  nombrePages: "",
                  prix: "",
                  prixInitial: "",
                  reductionPourcent: "0",
                  prixFinal: "",
                  gratuit: false,
                },
                ...g.documents,
              ],
            }
          : g
      ),
    }));
  };

  // ---------- upload helpers ----------
  const uploadImage = async (uri, folder) => {
    if (!uri) return null;
    const res = await fetch(uri);
    const blob = await res.blob();
    const storage = getStorage();
    const storageRef = ref(storage, `images/${folder}/${Date.now()}_${Math.random().toString(36).slice(2)}`);
    const task = uploadBytesResumable(storageRef, blob);
    return new Promise((resolve, reject) => {
      task.on(
        "state_changed",
        (s) => setUploadProgress((s.bytesTransferred / s.totalBytes) * 100),
        reject,
        async () => resolve(await getDownloadURL(task.snapshot.ref))
      );
    });
  };

  const uploadPDF = async (uri, folder, needsRestriction = false) => {
    if (!uri) return null;
    const res = await fetch(uri);
    const blob = await res.blob();
    const storage = getStorage();
    const storageRef = ref(storage, `documents/${folder}/${Date.now()}_${Math.random().toString(36).slice(2)}.pdf`);
    const task = uploadBytesResumable(storageRef, blob);
    
    return new Promise((resolve, reject) => {
      task.on(
        "state_changed",
        (s) => setUploadProgress((s.bytesTransferred / s.totalBytes) * 100),
        reject,
        async () => {
          const downloadURL = await getDownloadURL(task.snapshot.ref);

          // ⚠️ Fonctionnalité "restriction téléchargement" SUSPENDUE.
          // La conversion .firebasestorage.app -> .appspot.com produisait des URLs
          // cassées (404). On conserve désormais l'URL native retournée par Firebase,
          // qui fonctionne. Le paramètre needsRestriction est ignoré volontairement.
          resolve(downloadURL);
        }
      );
    });
  };

  // ---------- submit ----------
  const handleSubmit = async () => {
    if (!selectedType || !formValid) return;
    try {
      setLoading(true);
      setUploadProgress(0);
  
      let data = null;
      let collectionName = "";
  
      if (selectedType === "concours") {
        collectionName = "concours";
        const { restrictionTelechargement } = concoursData; // ✅ Extraire le flag
        const cover = concoursData.coverImage ? await uploadImage(concoursData.coverImage, "concours") : null;
  
        let documents = [];
        let groups = [];
        if (concoursData.hasGroups) {
          groups = await Promise.all(
            concoursData.groups.map(async (g) => ({
              ...g,
              documents: await Promise.all(
                g.documents.map(async (d) => ({
                  ...d,
                  document: await uploadPDF(d.document.uri, "concours", restrictionTelechargement), // ✅ Passer le flag
                }))
              ),
            }))
          );
        } else {
          documents = await Promise.all(
            concoursData.documents.map(async (d) => ({
              ...d,
              document: await uploadPDF(d.document.uri, "concours", restrictionTelechargement), // ✅ Passer le flag
            }))
          );
        }
  
        const arretes = await Promise.all(
          concoursData.arretes.map(async (a) => ({
            id: a.id,
            titre: a.titre,
            annee: a.annee,
            url: a.url || null,
            document: a.document?.uri ? await uploadPDF(a.document.uri, "arretes", restrictionTelechargement) : null, // ✅ Passer le flag
          }))
        );
  
        data = { ...concoursData, coverImage: cover, documents, groups, arretes };
      }
  
      if (selectedType === "bords") {
        collectionName = "bords";
        const { restrictionTelechargement } = bordsData; // ✅ Extraire le flag
        const imageUrl = bordsData.image ? await uploadImage(bordsData.image, "bords") : null;
        
        const docs = await Promise.all(
          bordsData.documents.map((d) => uploadPDF(d.uri, "bords", restrictionTelechargement)) // ✅ Passer le flag
        );
        
        const docsOptionnels = await Promise.all(
          bordsData.documentsOptionnels.map(async (d) => ({
            ...d,
            document: await uploadPDF(d.document.uri, "bords_optionnels", restrictionTelechargement), // ✅ Passer le flag
          }))
        );
        
        const echantillons = await Promise.all(bordsData.echantillons.map((u) => uploadImage(u, "echantillons")));
        const feuilletage = bordsData.feuilletage ? await uploadPDF(bordsData.feuilletage.uri, "feuilletage", restrictionTelechargement) : null; // ✅ Passer le flag
        
        // Upload des documents anglais
        const docsAnglais = await Promise.all(
          bordsData.documentsAnglais.map((d) => uploadPDF(d.uri, "bords_anglais", restrictionTelechargement))
        );

        data = {
          ...bordsData,
          image: imageUrl,
          documents: docs,
          documentsAnglais: docsAnglais,
          documentsOptionnels: docsOptionnels,
          echantillons,
          feuilletage
        };
      }
  
      if (selectedType === "simple") {
        collectionName = "documents";
        const { restrictionTelechargement } = simpleData; // ✅ Extraire le flag
        const docUrl = simpleData.document ? await uploadPDF(simpleData.document.uri, "simple", restrictionTelechargement) : null; // ✅ Passer le flag
        const cover = simpleData.coverImage ? await uploadImage(simpleData.coverImage, "simple") : null;
        data = { ...simpleData, document: docUrl, coverImage: cover, liens: simpleData.liens };
      }
  
      await addDoc(collection(db, collectionName), {
        ...data,
        createdAt: serverTimestamp(),
        status: "active",
      });
  
      alert("Document publié avec succès !");
      navigation.goBack?.();
    } catch (e) {
      console.error(e);
      alert("Erreur pendant la publication.");
    } finally {
      setLoading(false);
    }
  };





  // ---------- UI ----------
  const renderHeader = () => (
    <View style={[styles.header, { backgroundColor: theme.primary }]}>
      <TouchableOpacity style={[styles.backButton, { backgroundColor: "rgba(255,255,255,.2)" }]} onPress={() => navigation.goBack?.()}>
        <MaterialCommunityIcons name="arrow-left" size={24} color="#fff" />
      </TouchableOpacity>
      <Text style={[styles.headerTitle, { color: "#fff" }]}>Publier un Document</Text>
      <View style={styles.headerProgress}>
        <Text style={[styles.headerSubtitle, { color: "#fff", opacity: 0.9 }]}>{selectedType ? "Étape 2/2" : "Étape 1/2"}</Text>
        <View style={[styles.progressBar, { backgroundColor: "rgba(255,255,255,.2)" }]}>
          <View style={[styles.progress, { width: selectedType ? "100%" : "50%", backgroundColor: "#fff" }]} />
        </View>
      </View>
    </View>
  );

  const renderTypeSelection = () => {
    if (isWeb) {
      // Version dashboard : cartes blanches bordées avec icône en pastille de couleur brand
      return (
        <View style={{ maxWidth: 900, width: '100%', alignSelf: 'center' }}>
          <Text style={{ fontSize: 13, color: theme.textSecondary, marginBottom: 16, fontWeight: '600' }}>
            CHOISIR LE TYPE DE DOCUMENT
          </Text>
          <View style={{ flexDirection: 'row', flexWrap: 'wrap', gap: 16 }}>
            {documentTypes.map((t) => (
              <TouchableOpacity
                key={t.id}
                onPress={() => setSelectedType(t.id)}
                style={{
                  flex: 1,
                  minWidth: 260,
                  backgroundColor: theme.surface,
                  borderWidth: 1,
                  borderColor: theme.border,
                  borderRadius: 14,
                  padding: 20,
                  flexDirection: 'row',
                  alignItems: 'center',
                  gap: 16,
                  cursor: 'pointer',
                  transition: 'all 0.15s ease',
                }}
                activeOpacity={0.85}
              >
                <View
                  style={{
                    width: 52,
                    height: 52,
                    borderRadius: 12,
                    backgroundColor: theme.primary + '15',
                    alignItems: 'center',
                    justifyContent: 'center',
                  }}
                >
                  <MaterialCommunityIcons name={t.icon} size={26} color={theme.primary} />
                </View>
                <View style={{ flex: 1, minWidth: 0 }}>
                  <Text style={{ fontSize: 15, fontWeight: '700', color: theme.text, marginBottom: 4 }}>
                    {t.title}
                  </Text>
                  <Text style={{ fontSize: 12, color: theme.textSecondary, lineHeight: 17 }}>
                    {t.description}
                  </Text>
                </View>
                <MaterialCommunityIcons name="chevron-right" size={20} color={theme.textSecondary} />
              </TouchableOpacity>
            ))}
          </View>
        </View>
      );
    }
    return (
      <View style={styles.typeSelection}>
        {documentTypes.map((t) => (
          <TouchableOpacity key={t.id} style={styles.typeCard} onPress={() => setSelectedType(t.id)}>
            <LinearGradient colors={t.gradient} style={styles.typeCardContent} start={{ x: 0, y: 0 }} end={{ x: 1, y: 1 }}>
              <View style={[styles.typeIconContainer, { backgroundColor: "rgba(255,255,255,.2)" }]}>
                <MaterialCommunityIcons name={t.icon} size={32} color="#fff" />
              </View>
              <View style={styles.typeTextContainer}>
                <Text style={[styles.typeTitle, { color: "#fff" }]}>{t.title}</Text>
                <Text style={[styles.typeDescription, { color: "#fff" }]}>{t.description}</Text>
              </View>
              <MaterialCommunityIcons name="chevron-right" size={24} color="#fff" />
            </LinearGradient>
          </TouchableOpacity>
        ))}
      </View>
    );
  };

  // ----- Arrêtés (web : réordonner avec ↑/↓) -----
  const renderArretesWeb = () => (
    <View style={[styles.section, { backgroundColor: theme.surface, borderColor: theme.border }]}>
      <View style={styles.sectionHeader}>
        <Text style={[styles.sectionTitle, { color: theme.text }]}>Arrêtés du Concours</Text>
        <TouchableOpacity style={[styles.addSmallButton, { backgroundColor: theme.primary }]} onPress={addArrete}>
          <MaterialCommunityIcons name="plus" size={20} color="#fff" />
        </TouchableOpacity>
      </View>

      {concoursData.arretes.length === 0 ? (
        <TouchableOpacity style={[styles.emptyStateButton, { borderColor: theme.border }]} onPress={addArrete}>
          <MaterialCommunityIcons name="gavel" size={32} color={theme.textSecondary} />
          <Text style={[styles.emptyStateText, { color: theme.textSecondary }]}>Ajouter le premier arrêté</Text>
        </TouchableOpacity>
      ) : (
        concoursData.arretes.map((a, idx) => (
          <View key={a.id} style={[styles.arreteContainer, { backgroundColor: theme.background, borderColor: theme.border }]}>
            <View style={styles.arreteHeader}>
              <Text style={[styles.arreteTitle, { color: theme.text }]}>Arrêté</Text>
              <View style={{ flexDirection: "row", gap: 6 }}>
                <TouchableOpacity
                  style={styles.iconBtn}
                  onPress={() =>
                    setConcoursData((p) => ({ ...p, arretes: moveItem(p.arretes, idx, idx - 1) }))
                  }
                  disabled={idx === 0}
                >
                  <MaterialCommunityIcons name="chevron-up" size={22} color={idx === 0 ? theme.textSecondary : theme.text} />
                </TouchableOpacity>
                <TouchableOpacity
                  style={styles.iconBtn}
                  onPress={() =>
                    setConcoursData((p) => ({ ...p, arretes: moveItem(p.arretes, idx, idx + 1) }))
                  }
                  disabled={idx === concoursData.arretes.length - 1}
                >
                  <MaterialCommunityIcons
                    name="chevron-down"
                    size={22}
                    color={idx === concoursData.arretes.length - 1 ? theme.textSecondary : theme.text}
                  />
                </TouchableOpacity>
                <TouchableOpacity style={styles.deleteButton} onPress={() => removeArrete(a.id)}>
                  <MaterialCommunityIcons name="delete" size={20} color={theme.danger} />
                </TouchableOpacity>
              </View>
            </View>

            <TextInput
              style={[styles.input, { backgroundColor: theme.surface, color: theme.text, borderColor: theme.border }]}
              placeholder="Titre de l'arrêté"
              value={a.titre}
              onChangeText={(t) => updateArrete(a.id, "titre", t)}
              placeholderTextColor={theme.textSecondary}
            />
            <TextInput
              style={[styles.input, { backgroundColor: theme.surface, color: theme.text, borderColor: theme.border }]}
              placeholder="Année de l'arrêté"
              value={a.annee?.toString() ?? ""}
              onChangeText={(t) => updateArrete(a.id, "annee", t)}
              keyboardType="numeric"
              placeholderTextColor={theme.textSecondary}
            />

            <Text style={[styles.optionLabel, { color: theme.text }]}>Option 1: Télécharger un fichier</Text>
            <TouchableOpacity style={[styles.documentUpload, { borderColor: theme.border }]} onPress={() => pickArretePDF(a.id)}>
              <LinearGradient colors={[theme.primary + "20", theme.primary + "10"]} style={styles.documentUploadContent}>
                <MaterialCommunityIcons name={a.document ? "file-check" : "file-upload"} size={24} color={theme.primary} />
                <Text style={[styles.documentUploadText, { color: theme.text }]}>
                  {a.document ? a.document.name : "Télécharger l'arrêté (PDF)"}
                </Text>
              </LinearGradient>
            </TouchableOpacity>

            <Text style={[styles.optionLabel, { color: theme.text, marginTop: 12 }]}>Option 2: Ajouter un lien</Text>
            <TextInput
              style={[styles.input, { backgroundColor: theme.surface, color: theme.text, borderColor: theme.border }]}
              placeholder="Lien vers l'arrêté (URL)"
              value={a.url}
              onChangeText={(t) => updateArrete(a.id, "url", t)}
              placeholderTextColor={theme.textSecondary}
              autoCapitalize="none"
              keyboardType="url"
            />

            {(a.document || a.url) && (
              <View style={[styles.statusIndicator, { backgroundColor: theme.primary + "10" }]}>
                <MaterialCommunityIcons name="check-circle" size={16} color={theme.primary} />
                <Text style={[styles.statusText, { color: theme.primary }]}>
                  {a.document && a.url ? "Fichier et lien ajoutés" : a.document ? "Fichier ajouté" : "Lien ajouté"}
                </Text>
              </View>
            )}
          </View>
        ))
      )}
    </View>
  );

  // ----- Documents / Groupes (web : réordonner avec ↑/↓) -----
  const renderConcoursDocumentsWeb = () => (
    <View style={[styles.section, { backgroundColor: theme.surface, borderColor: theme.border }]}>
      <Text style={[styles.sectionTitle, { color: theme.text }]}>
        {concoursData.hasGroups ? "Groupes de Documents" : "Documents du Concours"}
      </Text>

      {concoursData.hasGroups ? (
        <>
          {concoursData.groups.map((g, gIdx) => (
            <View key={g.id} style={[styles.groupContainer, { backgroundColor: theme.background, borderColor: theme.border }]}>
              <View style={styles.groupHeader}>
                <View style={{ flex: 1 }}>
                  <TextInput
                    style={[styles.groupNameInput, { backgroundColor: theme.background, color: theme.text, borderColor: theme.border }]}
                    placeholder="Nom du groupe"
                    value={g.nom}
                    onChangeText={(t) => updateGroup(g.id, "nom", t)}
                    placeholderTextColor={theme.textSecondary}
                  />
                </View>
                <TouchableOpacity
                  style={styles.iconBtn}
                  onPress={() => setConcoursData((p) => ({ ...p, groups: moveItem(p.groups, gIdx, gIdx - 1) }))}
                  disabled={gIdx === 0}
                >
                  <MaterialCommunityIcons name="chevron-up" size={22} color={gIdx === 0 ? theme.textSecondary : theme.text} />
                </TouchableOpacity>
                <TouchableOpacity
                  style={styles.iconBtn}
                  onPress={() => setConcoursData((p) => ({ ...p, groups: moveItem(p.groups, gIdx, gIdx + 1) }))}
                  disabled={gIdx === concoursData.groups.length - 1}
                >
                  <MaterialCommunityIcons
                    name="chevron-down"
                    size={22}
                    color={gIdx === concoursData.groups.length - 1 ? theme.textSecondary : theme.text}
                  />
                </TouchableOpacity>
                <TouchableOpacity style={styles.deleteGroupButton} onPress={() => removeGroup(g.id)}>
                  <MaterialCommunityIcons name="delete" size={22} color={theme.danger} />
                </TouchableOpacity>
              </View>

              {g.documents.map((d, dIdx) => (
                <View key={d.id} style={[styles.documentContainer, { backgroundColor: theme.surface, borderColor: theme.border }]}>
                  <View style={styles.documentHeader}>
                    <MaterialCommunityIcons name="file-pdf-box" size={24} color={theme.primary} />
                    <Text style={[styles.documentTitle, { color: theme.text }]}>
                      {((d?.document?.name ?? d.titre) || "Document")}
                    </Text>
                    <TouchableOpacity
                      style={styles.iconBtn}
                      onPress={() =>
                        setConcoursData((p) => ({
                          ...p,
                          groups: p.groups.map((gg, i) =>
                            i !== gIdx ? gg : { ...gg, documents: moveItem(gg.documents, dIdx, dIdx - 1) }
                          ),
                        }))
                      }
                      disabled={dIdx === 0}
                    >
                      <MaterialCommunityIcons name="chevron-up" size={22} color={dIdx === 0 ? theme.textSecondary : theme.text} />
                    </TouchableOpacity>
                    <TouchableOpacity
                      style={styles.iconBtn}
                      onPress={() =>
                        setConcoursData((p) => ({
                          ...p,
                          groups: p.groups.map((gg, i) =>
                            i !== gIdx ? gg : { ...gg, documents: moveItem(gg.documents, dIdx, dIdx + 1) }
                          ),
                        }))
                      }
                      disabled={dIdx === g.documents.length - 1}
                    >
                      <MaterialCommunityIcons
                        name="chevron-down"
                        size={22}
                        color={dIdx === g.documents.length - 1 ? theme.textSecondary : theme.text}
                      />
                    </TouchableOpacity>
                    <TouchableOpacity
                      style={styles.deleteDocumentButton}
                      onPress={() =>
                        setConcoursData((p) => ({
                          ...p,
                          groups: p.groups.map((gg) =>
                            gg.id === g.id ? { ...gg, documents: gg.documents.filter((_, i) => i !== dIdx) } : gg
                          ),
                        }))
                      }
                    >
                      <MaterialCommunityIcons name="delete" size={20} color={theme.danger} />
                    </TouchableOpacity>
                  </View>

                  {/* champs */}
                  <TextInput
                    style={[styles.input, { backgroundColor: theme.background, color: theme.text, borderColor: theme.border }]}
                    placeholder="Titre du document"
                    value={d.titre}
                    onChangeText={(t) =>
                      setConcoursData((p) => ({
                        ...p,
                        groups: p.groups.map((gg) =>
                          gg.id === g.id
                            ? {
                                ...gg,
                                documents: gg.documents.map((dd, i) => (i === dIdx ? { ...dd, titre: t } : dd)),
                              }
                            : gg
                        ),
                      }))
                    }
                    placeholderTextColor={theme.textSecondary}
                  />
                  <TextInput
                    style={[styles.input, { backgroundColor: theme.background, color: theme.text, borderColor: theme.border }]}
                    placeholder="Filière"
                    value={d.filiere}
                    onChangeText={(t) =>
                      setConcoursData((p) => ({
                        ...p,
                        groups: p.groups.map((gg) =>
                          gg.id === g.id
                            ? {
                                ...gg,
                                documents: gg.documents.map((dd, i) => (i === dIdx ? { ...dd, filiere: t } : dd)),
                              }
                            : gg
                        ),
                      }))
                    }
                    placeholderTextColor={theme.textSecondary}
                  />

                  <View style={styles.row}>
                    <View style={styles.flex1}>
                      <TextInput
                        style={[styles.input, { backgroundColor: theme.background, color: theme.text, borderColor: theme.border }]}
                        placeholder="Année"
                        value={d.annee?.toString() ?? ""}
                        onChangeText={(t) =>
                          setConcoursData((p) => ({
                            ...p,
                            groups: p.groups.map((gg) =>
                              gg.id === g.id
                                ? {
                                    ...gg,
                                    documents: gg.documents.map((dd, i) => (i === dIdx ? { ...dd, annee: t } : dd)),
                                  }
                                : gg
                            ),
                          }))
                        }
                        keyboardType="numeric"
                        placeholderTextColor={theme.textSecondary}
                      />
                    </View>
                    <View style={[styles.flex1, styles.ml2]}>
                      <TextInput
                        style={[styles.input, { backgroundColor: theme.background, color: theme.text, borderColor: theme.border }]}
                        placeholder="Pages"
                        value={d.nombrePages}
                        onChangeText={(t) =>
                          setConcoursData((p) => ({
                            ...p,
                            groups: p.groups.map((gg) =>
                              gg.id === g.id
                                ? {
                                    ...gg,
                                    documents: gg.documents.map((dd, i) => (i === dIdx ? { ...dd, nombrePages: t } : dd)),
                                  }
                                : gg
                            ),
                          }))
                        }
                        keyboardType="numeric"
                        placeholderTextColor={theme.textSecondary}
                      />
                    </View>
                  </View>

                  <View style={styles.row}>
                    <View className="flex-1" style={styles.flex1}>
                      <TextInput
                        style={[styles.input, { backgroundColor: theme.background, color: theme.text, borderColor: theme.border }]}
                        placeholder="Prix (FCFA)"
                        value={d.prix}
                        editable={!d.gratuit}
                        onChangeText={(t) => handleConcoursPrixChange(g.id, dIdx, t)}
                        keyboardType="numeric"
                        placeholderTextColor={theme.textSecondary}
                      />
                    </View>
                    <View style={[styles.flex1, styles.ml2]}>
                      <TouchableOpacity
                        style={[
                          styles.gratuitButton,
                          { backgroundColor: theme.background, borderColor: theme.border },
                          d.gratuit && { backgroundColor: theme.primary, borderColor: theme.primary },
                        ]}
                        onPress={() =>
                          setConcoursData((p) => ({
                            ...p,
                            groups: p.groups.map((gg) =>
                              gg.id === g.id
                                ? {
                                    ...gg,
                                    documents: gg.documents.map((dd, i) =>
                                      i === dIdx ? { ...dd, gratuit: !dd.gratuit, prix: dd.gratuit ? "" : "0" } : dd
                                    ),
                                  }
                                : gg
                            ),
                          }))
                        }
                      >
                        <Text style={[styles.gratuitButtonText, { color: d.gratuit ? "#fff" : theme.text }]}>Gratuit</Text>
                      </TouchableOpacity>
                    </View>
                  </View>

                  <View style={styles.row}>
                    <View style={styles.flex1}>
                      <TextInput
                        style={[styles.input, { backgroundColor: theme.background, color: theme.text, borderColor: theme.border }]}
                        placeholder="Réduction (%)"
                        value={d.reductionPourcent || '0'}
                        editable={!d.gratuit}
                        onChangeText={(t) => handleConcoursReductionChange(g.id, dIdx, t)}
                        keyboardType="numeric"
                        placeholderTextColor={theme.textSecondary}
                      />
                    </View>
                    <View style={[styles.flex1, styles.ml2]}>
                      <TextInput
                        style={[styles.input, { backgroundColor: theme.background, color: theme.text, borderColor: theme.border }]}
                        placeholder="Prix final"
                        value={d.prixFinal || d.prix}
                        editable={false}
                        placeholderTextColor={theme.textSecondary}
                      />
                    </View>
                  </View>

                  {parseFloat(d.reductionPourcent || 0) > 0 && (
                    <View style={[styles.statusIndicator, { backgroundColor: theme.primary + '10' }]}>
                      <MaterialCommunityIcons name="sale" size={16} color={theme.primary} />
                      <Text style={[styles.statusText, { color: theme.primary }]}>
                        Réduction de {d.reductionPourcent}% appliquée - Prix final: {d.prixFinal} FCFA
                      </Text>
                    </View>
                  )}
                </View>
              ))}

              <TouchableOpacity style={styles.addButton} onPress={() => addDocumentToGroup(g.id)}>
                <LinearGradient colors={[theme.primary + "20", theme.primary + "10"]} style={styles.addButtonContent}>
                  <MaterialCommunityIcons name="file-plus" size={24} color={theme.primary} />
                  <Text style={[styles.addButtonText, { color: theme.primary }]}>Ajouter un document au groupe</Text>
                </LinearGradient>
              </TouchableOpacity>
            </View>
          ))}

          <TouchableOpacity style={[styles.addButton, { marginTop: 20 }]} onPress={addGroup}>
            <LinearGradient colors={[theme.primary + "20", theme.primary + "10"]} style={styles.addButtonContent}>
              <MaterialCommunityIcons name="account-group-outline" size={24} color={theme.primary} />
              <Text style={[styles.addButtonText, { color: theme.primary }]}>Ajouter un nouveau groupe</Text>
            </LinearGradient>
          </TouchableOpacity>
        </>
      ) : (
        <>
          {concoursData.documents.map((d, idx) => (
            <View key={d.id} style={[styles.documentContainer, { backgroundColor: theme.surface, borderColor: theme.border }]}>
              <View style={styles.documentHeader}>
                <MaterialCommunityIcons name="file-pdf-box" size={24} color={theme.primary} />
                <Text style={[styles.documentTitle, { color: theme.text }]}>
                  {((d?.document?.name ?? d.titre) || "Document")}
                </Text>
                <TouchableOpacity
                  style={styles.iconBtn}
                  onPress={() => setConcoursData((p) => ({ ...p, documents: moveItem(p.documents, idx, idx - 1) }))}
                  disabled={idx === 0}
                >
                  <MaterialCommunityIcons name="chevron-up" size={22} color={idx === 0 ? theme.textSecondary : theme.text} />
                </TouchableOpacity>
                <TouchableOpacity
                  style={styles.iconBtn}
                  onPress={() => setConcoursData((p) => ({ ...p, documents: moveItem(p.documents, idx, idx + 1) }))}
                  disabled={idx === concoursData.documents.length - 1}
                >
                  <MaterialCommunityIcons
                    name="chevron-down"
                    size={22}
                    color={idx === concoursData.documents.length - 1 ? theme.textSecondary : theme.text}
                  />
                </TouchableOpacity>
                <TouchableOpacity
                  style={styles.deleteDocumentButton}
                  onPress={() =>
                    setConcoursData((p) => ({ ...p, documents: p.documents.filter((_, i) => i !== idx) }))
                  }
                >
                  <MaterialCommunityIcons name="delete" size={20} color={theme.danger} />
                </TouchableOpacity>
              </View>

              <TextInput
                style={[styles.input, { backgroundColor: theme.background, color: theme.text, borderColor: theme.border }]}
                placeholder="Titre du document"
                value={d.titre}
                onChangeText={(t) =>
                  setConcoursData((p) => ({
                    ...p,
                    documents: p.documents.map((dd, i) => (i === idx ? { ...dd, titre: t } : dd)),
                  }))
                }
                placeholderTextColor={theme.textSecondary}
              />
              <TextInput
                style={[styles.input, { backgroundColor: theme.background, color: theme.text, borderColor: theme.border }]}
                placeholder="Filière"
                value={d.filiere}
                onChangeText={(t) =>
                  setConcoursData((p) => ({
                    ...p,
                    documents: p.documents.map((dd, i) => (i === idx ? { ...dd, filiere: t } : dd)),
                  }))
                }
                placeholderTextColor={theme.textSecondary}
              />

              <View style={styles.row}>
                <View style={styles.flex1}>
                  <TextInput
                    style={[styles.input, { backgroundColor: theme.background, color: theme.text, borderColor: theme.border }]}
                    placeholder="Année"
                    value={d.annee?.toString() ?? ""}
                    onChangeText={(t) =>
                      setConcoursData((p) => ({
                        ...p,
                        documents: p.documents.map((dd, i) => (i === idx ? { ...dd, annee: t } : dd)),
                      }))
                    }
                    keyboardType="numeric"
                    placeholderTextColor={theme.textSecondary}
                  />
                </View>
                <View style={[styles.flex1, styles.ml2]}>
                  <TextInput
                    style={[styles.input, { backgroundColor: theme.background, color: theme.text, borderColor: theme.border }]}
                    placeholder="Pages"
                    value={d.nombrePages}
                    onChangeText={(t) =>
                      setConcoursData((p) => ({
                        ...p,
                        documents: p.documents.map((dd, i) => (i === idx ? { ...dd, nombrePages: t } : dd)),
                      }))
                    }
                    keyboardType="numeric"
                    placeholderTextColor={theme.textSecondary}
                  />
                </View>
              </View>

              <View style={styles.row}>
                <View style={styles.flex1}>
                  <TextInput
                    style={[styles.input, { backgroundColor: theme.background, color: theme.text, borderColor: theme.border }]}
                    placeholder="Prix (FCFA)"
                    value={d.prix}
                    editable={!d.gratuit}
                    onChangeText={(t) => handleConcoursDocPrixChange(idx, t)}
                    keyboardType="numeric"
                    placeholderTextColor={theme.textSecondary}
                  />
                </View>
                <View style={[styles.flex1, styles.ml2]}>
                  <TouchableOpacity
                    style={[
                      styles.gratuitButton,
                      { backgroundColor: theme.background, borderColor: theme.border },
                      d.gratuit && { backgroundColor: theme.primary, borderColor: theme.primary },
                    ]}
                    onPress={() =>
                      setConcoursData((p) => ({
                        ...p,
                        documents: p.documents.map((dd, i) =>
                          i === idx ? { ...dd, gratuit: !dd.gratuit, prix: dd.gratuit ? "" : "0" } : dd
                        ),
                      }))
                    }
                  >
                    <Text style={[styles.gratuitButtonText, { color: d.gratuit ? "#fff" : theme.text }]}>Gratuit</Text>
                  </TouchableOpacity>
                </View>
              </View>

              <View style={styles.row}>
                <View style={styles.flex1}>
                  <TextInput
                    style={[styles.input, { backgroundColor: theme.background, color: theme.text, borderColor: theme.border }]}
                    placeholder="Réduction (%)"
                    value={d.reductionPourcent || '0'}
                    editable={!d.gratuit}
                    onChangeText={(t) => handleConcoursDocReductionChange(idx, t)}
                    keyboardType="numeric"
                    placeholderTextColor={theme.textSecondary}
                  />
                </View>
                <View style={[styles.flex1, styles.ml2]}>
                  <TextInput
                    style={[styles.input, { backgroundColor: theme.background, color: theme.text, borderColor: theme.border }]}
                    placeholder="Prix final"
                    value={d.prixFinal || d.prix}
                    editable={false}
                    placeholderTextColor={theme.textSecondary}
                  />
                </View>
              </View>

              {parseFloat(d.reductionPourcent || 0) > 0 && (
                <View style={[styles.statusIndicator, { backgroundColor: theme.primary + '10' }]}>
                  <MaterialCommunityIcons name="sale" size={16} color={theme.primary} />
                  <Text style={[styles.statusText, { color: theme.primary }]}>
                    Réduction de {d.reductionPourcent}% appliquée - Prix final: {d.prixFinal} FCFA
                  </Text>
                </View>
              )}
            </View>
          ))}



          <TouchableOpacity style={styles.addButton} onPress={pickPDF}>
            <LinearGradient colors={[theme.primary + "20", theme.primary + "10"]} style={styles.addButtonContent}>
              <MaterialCommunityIcons name="file-plus" size={24} color={theme.primary} />
              <Text style={[styles.addButtonText, { color: theme.primary }]}>Ajouter un document</Text>
            </LinearGradient>
          </TouchableOpacity>
        </>
      )}
    </View>
  );

  const renderConcoursForm = () => (
    <View style={styles.formContainer}>
      <View style={[styles.section, { backgroundColor: theme.surface, borderColor: theme.border }]}>
        <Text style={[styles.sectionTitle, { color: theme.text }]}>Informations du Concours</Text>

        <TouchableOpacity style={styles.imageUploadContainer} onPress={() => pickImage("concours")}>
          {concoursData.coverImage ? (
            <>
              <Image source={{ uri: concoursData.coverImage }} style={styles.uploadedImage} resizeMode="cover" />
              <View style={[styles.uploadOverlay, { backgroundColor: "rgba(0,0,0,.3)" }]}>
                <MaterialCommunityIcons name="camera-plus" size={40} color="#fff" />
              </View>
            </>
          ) : (
            <LinearGradient colors={["rgba(0,0,0,.05)", "rgba(0,0,0,.1)"]} style={styles.uploadPlaceholder}>
              <MaterialCommunityIcons name="image-plus" size={40} color={theme.primary} />
              <Text style={[styles.uploadText, { color: theme.textSecondary }]}>Ajouter une image de couverture</Text>
            </LinearGradient>
          )}
        </TouchableOpacity>

        <TextInput
          style={[styles.input, { backgroundColor: theme.background, color: theme.text, borderColor: theme.border }]}
          placeholder="Sigle du concours (ex: ENAM)"
          value={concoursData.sigle}
          onChangeText={(t) => setConcoursData((p) => ({ ...p, sigle: t }))}
          placeholderTextColor={theme.textSecondary}
        />
        <TextInput
          style={[styles.input, { backgroundColor: theme.background, color: theme.text, borderColor: theme.border }]}
          placeholder="Nom complet du concours"
          value={concoursData.nomComplet}
          onChangeText={(t) => setConcoursData((p) => ({ ...p, nomComplet: t }))}
          placeholderTextColor={theme.textSecondary}
        />
        <TextInput
          style={[styles.input, { backgroundColor: theme.background, color: theme.text, borderColor: theme.border }]}
          placeholder="Type du concours"
          value={concoursData.typeConcours}
          onChangeText={(t) => setConcoursData((p) => ({ ...p, typeConcours: t }))}
          placeholderTextColor={theme.textSecondary}
        />
        <TextInput
          style={[styles.input, { backgroundColor: theme.background, color: theme.text, borderColor: theme.border }]}
          placeholder="Niveau requis"
          value={concoursData.niveau}
          onChangeText={(t) => setConcoursData((p) => ({ ...p, niveau: t }))}
          placeholderTextColor={theme.textSecondary}
        />

        <View style={[styles.typeSelector, { backgroundColor: theme.background }]}>
          <TouchableOpacity
            style={[styles.typeOption, concoursData.langue === "fr" && { backgroundColor: theme.primary }]}
            onPress={() => setConcoursData((p) => ({ ...p, langue: "fr" }))}
          >
            <Text style={[styles.typeOptionText, { color: concoursData.langue === "fr" ? "#fff" : theme.textSecondary }]}>
              Français
            </Text>
          </TouchableOpacity>
          <TouchableOpacity
            style={[styles.typeOption, concoursData.langue === "en" && { backgroundColor: theme.primary }]}
            onPress={() => setConcoursData((p) => ({ ...p, langue: "en" }))}
          >
            <Text style={[styles.typeOptionText, { color: concoursData.langue === "en" ? "#fff" : theme.textSecondary }]}>
              Anglais
            </Text>
          </TouchableOpacity>
        </View>

        <RichTextEditor
          value={concoursData.description}
          onChange={(t) => setConcoursData((p) => ({ ...p, description: t }))}
          placeholder="Description du document"
          style={{ marginBottom: 16 }}
          theme={theme}
        />

        <View style={[styles.typeSelector, { backgroundColor: theme.background, marginBottom: 20 }]}>
          <TouchableOpacity
            style={[styles.typeOption, !concoursData.hasGroups && { backgroundColor: theme.primary }]}
            onPress={() => setConcoursData((p) => ({ ...p, hasGroups: false }))}
          >
            <Text style={[styles.typeOptionText, { color: !concoursData.hasGroups ? "#fff" : theme.textSecondary }]}>
              Sans Groupes
            </Text>
          </TouchableOpacity>
          <TouchableOpacity
            style={[styles.typeOption, concoursData.hasGroups && { backgroundColor: theme.primary }]}
            onPress={() => setConcoursData((p) => ({ ...p, hasGroups: true }))}
          >
            <Text style={[styles.typeOptionText, { color: concoursData.hasGroups ? "#fff" : theme.textSecondary }]}>
              Avec Groupes
            </Text>
          </TouchableOpacity>
        </View>
      </View>


      {renderArretesWeb()}
      {renderConcoursDocumentsWeb()}

    {RESTRICTION_FEATURE_ENABLED && (
    <View style={[styles.restrictionContainer, { backgroundColor: theme.surface, borderColor: theme.border }]}>
      <View style={styles.restrictionInfo}>
        <MaterialCommunityIcons name="lock-outline" size={24} color={theme.primary} />
        <View style={styles.restrictionTextContainer}>
          <Text style={[styles.restrictionTitle, { color: theme.text }]}>
            Restriction de téléchargement
          </Text>
          <Text style={[styles.restrictionDescription, { color: theme.textSecondary }]}>
            Si activée, les utilisateurs ne pourront pas télécharger le document, seulement le consulter dans l'app
          </Text>
        </View>
      </View>
      <input
        type="checkbox"
        checked={concoursData.restrictionTelechargement}
        onChange={(e) => setConcoursData((p) => ({ ...p, restrictionTelechargement: e.target.checked }))}
        style={{ width: 48, height: 24, cursor: "pointer" }}
      />
    </View>
    )}



      <TouchableOpacity
        style={[styles.submitButton, (!formValid || loading) && styles.submitButtonDisabled]}
        onPress={handleSubmit}
        disabled={!formValid || loading}
      >
        <LinearGradient colors={[theme.primary, theme.primaryDark]} style={styles.submitButtonContent}>
          {loading ? <ActivityIndicator color="#fff" /> : <Text style={[styles.submitText, { color: "#fff" }]}>Publier le Concours</Text>}
        </LinearGradient>
      </TouchableOpacity>
    </View>
  );

  const renderBordsForm = () => (
    <View style={styles.formContainer}>
      <View style={[styles.section, { backgroundColor: theme.surface, borderColor: theme.border }]}>
        <Text style={[styles.sectionTitle, { color: theme.text }]}>Informations du Bord</Text>

        <TouchableOpacity style={styles.imageUploadContainer} onPress={() => pickImage("bords")}>
          {bordsData.image ? (
            <>
              <Image source={{ uri: bordsData.image }} style={styles.uploadedImage} resizeMode="cover" />
              <View style={[styles.uploadOverlay, { backgroundColor: "rgba(0,0,0,.3)" }]}>
                <MaterialCommunityIcons name="camera-plus" size={40} color="#fff" />
              </View>
            </>
          ) : (
            <LinearGradient colors={["rgba(0,0,0,.05)", "rgba(0,0,0,.1)"]} style={styles.uploadPlaceholder}>
              <MaterialCommunityIcons name="image-plus" size={40} color={theme.primary} />
              <Text style={[styles.uploadText, { color: theme.textSecondary }]}>Ajouter une image de couverture</Text>
            </LinearGradient>
          )}
        </TouchableOpacity>

        <TextInput
          style={[styles.input, { backgroundColor: theme.background, color: theme.text, borderColor: theme.border }]}
          placeholder="Titre du bord"
          value={bordsData.titre}
          onChangeText={(t) => setBordsData((p) => ({ ...p, titre: t }))}
          placeholderTextColor={theme.textSecondary}
        />

        <TextInput
          style={[styles.input, { backgroundColor: theme.background, color: theme.text, borderColor: theme.border }]}
          placeholder="Nom du document"
          value={bordsData.nomDocument}
          onChangeText={(t) => setBordsData((p) => ({ ...p, nomDocument: t }))}
          placeholderTextColor={theme.textSecondary}
        />

        <RichTextEditor
          value={bordsData.description}
          onChange={(t) => setBordsData((p) => ({ ...p, description: t }))}
          placeholder="Description détaillée"
          style={{ marginBottom: 16 }}
          theme={theme}
        />

        <View style={styles.row}>
          <View style={styles.flex1}>
            <TextInput
              style={[styles.input, { backgroundColor: theme.background, color: theme.text, borderColor: theme.border }]}
              placeholder="Prix (FCFA)"
              value={bordsData.prix}
              onChangeText={handleBordsPrixChange}
              keyboardType="numeric"
              placeholderTextColor={theme.textSecondary}
            />
          </View>
          <View style={[styles.flex1, styles.ml2]}>
            <TextInput
              style={[styles.input, { backgroundColor: theme.background, color: theme.text, borderColor: theme.border }]}
              placeholder="Nombre de pages"
              value={bordsData.nombrePages}
              onChangeText={(t) => setBordsData((p) => ({ ...p, nombrePages: t }))}
              keyboardType="numeric"
              placeholderTextColor={theme.textSecondary}
            />
          </View>
        </View>

        <View style={styles.row}>
          <View style={styles.flex1}>
            <TextInput
              style={[styles.input, { backgroundColor: theme.background, color: theme.text, borderColor: theme.border }]}
              placeholder="Réduction (%)"
              value={bordsData.reductionPourcent}
              onChangeText={handleBordsReductionChange}
              keyboardType="numeric"
              placeholderTextColor={theme.textSecondary}
            />
          </View>
          <View style={[styles.flex1, styles.ml2]}>
            <TextInput
              style={[styles.input, { backgroundColor: theme.background, color: theme.text, borderColor: theme.border }]}
              placeholder="Prix final"
              value={bordsData.prixFinal}
              editable={false}
              placeholderTextColor={theme.textSecondary}
            />
          </View>
        </View>

        {parseFloat(bordsData.reductionPourcent) > 0 && (
          <View style={[styles.statusIndicator, { backgroundColor: theme.primary + '10', marginBottom: 16 }]}>
            <MaterialCommunityIcons name="sale" size={16} color={theme.primary} />
            <Text style={[styles.statusText, { color: theme.primary }]}>
              Réduction de {bordsData.reductionPourcent}% appliquée - Prix final: {bordsData.prixFinal} FCFA
            </Text>
          </View>
        )}

        <View style={[styles.typeSelector, { backgroundColor: theme.background }]}>
          <TouchableOpacity
            style={[styles.typeOption, bordsData.langue === "fr" && { backgroundColor: theme.primary }]}
            onPress={() => setBordsData((p) => ({ ...p, langue: "fr" }))}
          >
            <Text style={[styles.typeOptionText, { color: bordsData.langue === "fr" ? "#fff" : theme.textSecondary }]}>Français</Text>
          </TouchableOpacity>
          <TouchableOpacity
            style={[styles.typeOption, bordsData.langue === "en" && { backgroundColor: theme.primary }]}
            onPress={() => setBordsData((p) => ({ ...p, langue: "en" }))}
          >
            <Text style={[styles.typeOptionText, { color: bordsData.langue === "en" ? "#fff" : theme.textSecondary }]}>Anglais</Text>
          </TouchableOpacity>
        </View>
      </View>

      <View style={[styles.section, { backgroundColor: theme.surface, borderColor: theme.border }]}>
        <Text style={[styles.sectionTitle, { color: theme.text }]}>Document Principal</Text>
        <TouchableOpacity style={[styles.documentUpload, { borderColor: theme.border }]} onPress={pickPDF}>
          <LinearGradient colors={[theme.primary + "20", theme.primary + "10"]} style={styles.documentUploadContent}>
            <MaterialCommunityIcons name="file-upload" size={30} color={theme.primary} />
            <Text style={[styles.documentUploadText, { color: theme.text }]}>Télécharger le bord (PDF)</Text>
          </LinearGradient>
        </TouchableOpacity>

        {bordsData.documents.map((doc, i) => (
          <View key={`${doc.name}-${i}`} style={[styles.documentItem, { backgroundColor: theme.background, borderColor: theme.border }]}>
            <MaterialCommunityIcons name="file-pdf-box" size={24} color={theme.primary} />
            <Text style={[styles.documentName, { color: theme.text }]}>{doc.name}</Text>
            <TouchableOpacity onPress={() => setBordsData((p) => ({ ...p, documents: p.documents.filter((_, x) => x !== i) }))}>
              <MaterialCommunityIcons name="delete" size={20} color={theme.danger} />
            </TouchableOpacity>
          </View>
        ))}
      </View>

      {/* Version Anglaise du Bord */}
      <View style={[styles.section, { backgroundColor: theme.surface, borderColor: theme.border }]}>
        <Text style={[styles.sectionTitle, { color: theme.text }]}>Version Anglaise (Optionnel)</Text>
        <Text style={[styles.formattingHint, { color: theme.textSecondary, marginBottom: 12 }]}>
          Ajoutez la version anglaise du bord si disponible. L'achat donne accès aux deux versions.
        </Text>
        <TouchableOpacity
          style={[styles.documentUpload, { borderColor: theme.border }]}
          onPress={async () => {
            try {
              const res = await DocumentPicker.getDocumentAsync({ type: "application/pdf", multiple: false, copyToCacheDirectory: true });
              if (res.canceled || !res.assets?.[0]) return;
              setBordsData((p) => ({ ...p, documentsAnglais: [...p.documentsAnglais, res.assets[0]] }));
            } catch { alert("Erreur : Impossible de sélectionner le document"); }
          }}
        >
          <LinearGradient colors={[theme.primary + "20", theme.primary + "10"]} style={styles.documentUploadContent}>
            <MaterialCommunityIcons name="translate" size={30} color={theme.primary} />
            <Text style={[styles.documentUploadText, { color: theme.text }]}>Télécharger la version anglaise (PDF)</Text>
          </LinearGradient>
        </TouchableOpacity>

        {bordsData.documentsAnglais.map((doc, i) => (
          <View key={`ang-${doc.name}-${i}`} style={[styles.documentItem, { backgroundColor: theme.background, borderColor: theme.border }]}>
            <MaterialCommunityIcons name="file-pdf-box" size={24} color={theme.primary} />
            <Text style={[styles.documentName, { color: theme.text }]}>{doc.name || 'Version anglaise'}</Text>
            <TouchableOpacity onPress={() => setBordsData((p) => ({ ...p, documentsAnglais: p.documentsAnglais.filter((_, x) => x !== i) }))}>
              <MaterialCommunityIcons name="delete" size={20} color={theme.danger} />
            </TouchableOpacity>
          </View>
        ))}
      </View>

      {/* Documents additionnels */}
      <View style={[styles.section, { backgroundColor: theme.surface, borderColor: theme.border }]}>
        <View style={styles.sectionHeader}>
          <Text style={[styles.sectionTitle, { color: theme.text }]}>Documents additionnels</Text>
          <TouchableOpacity style={[styles.addSmallButton, { backgroundColor: theme.primary }]} onPress={pickDocumentOptionnel}>
            <MaterialCommunityIcons name="plus" size={20} color="#fff" />
          </TouchableOpacity>
        </View>

        {bordsData.documentsOptionnels.length === 0 ? (
          <TouchableOpacity style={[styles.emptyStateButton, { borderColor: theme.border }]} onPress={pickDocumentOptionnel}>
            <MaterialCommunityIcons name="file-plus" size={32} color={theme.textSecondary} />
            <Text style={[styles.emptyStateText, { color: theme.textSecondary }]}>Ajouter un document additionnel</Text>
          </TouchableOpacity>
        ) : (
          bordsData.documentsOptionnels.map((doc) => (
            <View key={doc.id} style={[styles.documentContainer, { backgroundColor: theme.background, borderColor: theme.border }]}>
              <View style={styles.documentHeader}>
                <MaterialCommunityIcons name="file-pdf-box" size={24} color={theme.primary} />
                <Text style={[styles.documentTitle, { color: theme.text }]}>
                  {doc.document?.name || "Document additionnel"}
                </Text>
                <TouchableOpacity style={styles.deleteDocumentButton} onPress={() => removeDocumentOptionnel(doc.id)}>
                  <MaterialCommunityIcons name="delete" size={20} color={theme.danger} />
                </TouchableOpacity>
              </View>

              <TextInput
                style={[styles.input, { backgroundColor: theme.surface, color: theme.text, borderColor: theme.border }]}
                placeholder="Titre du document"
                value={doc.titre}
                onChangeText={(t) => updateDocumentOptionnel(doc.id, "titre", t)}
                placeholderTextColor={theme.textSecondary}
              />

              <TextInput
                style={[styles.input, styles.textArea, { backgroundColor: theme.surface, color: theme.text, borderColor: theme.border }]}
                placeholder="Description du document"
                value={doc.description}
                onChangeText={(t) => updateDocumentOptionnel(doc.id, "description", t)}
                multiline
                numberOfLines={2}
                placeholderTextColor={theme.textSecondary}
              />

              {/* Prix optionnel : vide = document inclus / gratuit */}
              <TextInput
                style={[styles.input, { backgroundColor: theme.surface, color: theme.text, borderColor: theme.border }]}
                placeholder="Prix (FCFA) — laisser vide pour inclus/gratuit"
                value={doc.prix || ""}
                onChangeText={(t) => updateDocumentOptionnel(doc.id, "prix", t.replace(/[^0-9]/g, ""))}
                keyboardType="numeric"
                placeholderTextColor={theme.textSecondary}
              />
            </View>
          ))
        )}
      </View>

      {/* Nouveau: Liens Clickables */}
      <View style={[styles.section, { backgroundColor: theme.surface, borderColor: theme.border }]}>
        <View style={styles.sectionHeader}>
          <Text style={[styles.sectionTitle, { color: theme.text }]}>Liens Utiles</Text>
          <TouchableOpacity style={[styles.addSmallButton, { backgroundColor: theme.primary }]} onPress={() => addLien("bords")}>
            <MaterialCommunityIcons name="plus" size={20} color="#fff" />
          </TouchableOpacity>
        </View>

        {bordsData.liens.length === 0 ? (
          <TouchableOpacity style={[styles.emptyStateButton, { borderColor: theme.border }]} onPress={() => addLien("bords")}>
            <MaterialCommunityIcons name="link-plus" size={32} color={theme.textSecondary} />
            <Text style={[styles.emptyStateText, { color: theme.textSecondary }]}>Ajouter un lien utile</Text>
          </TouchableOpacity>
        ) : (
          bordsData.liens.map((lien) => (
            <View key={lien.id} style={[styles.lienContainer, { backgroundColor: theme.background, borderColor: theme.border }]}>
              <View style={styles.lienHeader}>
                <MaterialCommunityIcons name="link" size={24} color={theme.primary} />
                <Text style={[styles.lienTitle, { color: theme.text }]}>Lien</Text>
                <TouchableOpacity style={styles.deleteButton} onPress={() => removeLien("bords", lien.id)}>
                  <MaterialCommunityIcons name="delete" size={20} color={theme.danger} />
                </TouchableOpacity>
              </View>

              <TextInput
                style={[styles.input, { backgroundColor: theme.surface, color: theme.text, borderColor: theme.border }]}
                placeholder="Titre du lien"
                value={lien.titre}
                onChangeText={(t) => updateLien("bords", lien.id, "titre", t)}
                placeholderTextColor={theme.textSecondary}
              />

              <TextInput
                style={[styles.input, { backgroundColor: theme.surface, color: theme.text, borderColor: theme.border }]}
                placeholder="URL du lien"
                value={lien.url}
                onChangeText={(t) => updateLien("bords", lien.id, "url", t)}
                placeholderTextColor={theme.textSecondary}
                autoCapitalize="none"
                keyboardType="url"
              />

              <TextInput
                style={[styles.input, styles.textArea, { backgroundColor: theme.surface, color: theme.text, borderColor: theme.border }]}
                placeholder="Description du lien"
                value={lien.description}
                onChangeText={(t) => updateLien("bords", lien.id, "description", t)}
                multiline
                numberOfLines={2}
                placeholderTextColor={theme.textSecondary}
              />
            </View>
          ))
        )}
      </View>

      <View style={[styles.section, { backgroundColor: theme.surface, borderColor: theme.border }]}>
        <Text style={[styles.sectionTitle, { color: theme.text }]}>Échantillons</Text>
        <ScrollView horizontal showsHorizontalScrollIndicator={false} style={styles.echantillonsContainer}>
          {bordsData.echantillons.map((uri, i) => (
            <View key={`${uri}-${i}`} style={styles.echantillonWrapper}>
              <Image source={{ uri }} style={styles.echantillonImage} resizeMode="cover" />
              <TouchableOpacity
                style={[styles.deleteEchantillonButton, { backgroundColor: "rgba(255,255,255,.9)" }]}
                onPress={() => setBordsData((p) => ({ ...p, echantillons: p.echantillons.filter((_, x) => x !== i) }))}
              >
                <MaterialCommunityIcons name="close-circle" size={24} color={theme.danger} />
              </TouchableOpacity>
            </View>
          ))}

          <TouchableOpacity style={styles.addEchantillonButton} onPress={() => pickImage("bords", true)}>
            <LinearGradient colors={[theme.primary + "20", theme.primary + "10"]} style={styles.addEchantillonContent}>
              <MaterialCommunityIcons name="image-plus" size={32} color={theme.primary} />
            </LinearGradient>
          </TouchableOpacity>
        </ScrollView>
      </View>

      <View style={[styles.section, { backgroundColor: theme.surface, borderColor: theme.border }]}>
        <Text style={[styles.sectionTitle, { color: theme.text }]}>Document de Feuilletage (Optionnel)</Text>
        <Text style={[styles.description, { color: theme.textSecondary, marginBottom: 12 }]}>
          Ajoutez un document PDF permettant aux acheteurs de feuilleter quelques pages avant l'achat
        </Text>

        <TouchableOpacity style={[styles.documentUpload, { borderColor: theme.border }]} onPress={pickFeuilletagePDF}>
          <LinearGradient colors={[theme.primary + "20", theme.primary + "10"]} style={styles.documentUploadContent}>
            <MaterialCommunityIcons name={bordsData.feuilletage ? "file-check" : "file-upload"} size={30} color={theme.primary} />
            <Text style={[styles.documentUploadText, { color: theme.text }]}>
              {bordsData.feuilletage ? bordsData.feuilletage.name : "Ajouter un document de feuilletage (PDF)"}
            </Text>
          </LinearGradient>
        </TouchableOpacity>

        {bordsData.feuilletage && (
          <TouchableOpacity
            style={[styles.removeFeuilletage, { borderColor: theme.border, marginTop: 8 }]}
            onPress={() => setBordsData((p) => ({ ...p, feuilletage: null }))}
          >
            <MaterialCommunityIcons name="delete" size={20} color={theme.danger} />
            <Text style={[styles.removeFeuilletageText, { color: theme.danger, marginLeft: 8 }]}>
              Supprimer le document de feuilletage
            </Text>
          </TouchableOpacity>
        )}
      </View>

      {/* Option de restriction de téléchargement — SUSPENDUE */}
{RESTRICTION_FEATURE_ENABLED && (
<View style={[styles.restrictionContainer, { backgroundColor: theme.surface, borderColor: theme.border }]}>
  <View style={styles.restrictionInfo}>
    <MaterialCommunityIcons name="lock-outline" size={24} color={theme.primary} />
    <View style={styles.restrictionTextContainer}>
      <Text style={[styles.restrictionTitle, { color: theme.text }]}>
        Restriction de téléchargement
      </Text>
      <Text style={[styles.restrictionDescription, { color: theme.textSecondary }]}>
        Si activée, les utilisateurs ne pourront pas télécharger le document, seulement le consulter dans l'app
      </Text>
    </View>
  </View>
  <input
    type="checkbox"
    checked={bordsData.restrictionTelechargement}
    onChange={(e) => setBordsData((p) => ({ ...p, restrictionTelechargement: e.target.checked }))}
    style={{ width: 48, height: 24, cursor: "pointer" }}
  />
</View>
)}

      <TouchableOpacity
        style={[styles.submitButton, (!formValid || loading) && styles.submitButtonDisabled]}
        onPress={handleSubmit}
        disabled={!formValid || loading}
      >
        <LinearGradient colors={[theme.primary, theme.primaryDark]} style={styles.submitButtonContent}>
          {loading ? <ActivityIndicator color="#fff" /> : <Text style={[styles.submitText, { color: "#fff" }]}>Publier le Bord</Text>}
        </LinearGradient>
      </TouchableOpacity>
    </View>
  );

  const renderSimpleForm = () => (
    <View style={styles.formContainer}>
      <View style={[styles.section, { backgroundColor: theme.surface, borderColor: theme.border }]}>
        <Text style={[styles.sectionTitle, { color: theme.text }]}>Document Simple</Text>

        <TouchableOpacity style={styles.imageUploadContainer} onPress={() => pickImage("simple")}>
          {simpleData.coverImage ? (
            <>
              <Image source={{ uri: simpleData.coverImage }} style={styles.uploadedImage} resizeMode="cover" />
              <View style={[styles.uploadOverlay, { backgroundColor: "rgba(0,0,0,.3)" }]}>
                <MaterialCommunityIcons name="camera-plus" size={40} color="#fff" />
              </View>
            </>
          ) : (
            <LinearGradient colors={["rgba(0,0,0,.05)", "rgba(0,0,0,.1)"]} style={styles.uploadPlaceholder}>
              <MaterialCommunityIcons name="image-plus" size={40} color={theme.primary} />
              <Text style={[styles.uploadText, { color: theme.textSecondary }]}>Ajouter une image de couverture</Text>
            </LinearGradient>
          )}
        </TouchableOpacity>

        <TextInput
          style={[styles.input, { backgroundColor: theme.background, color: theme.text, borderColor: theme.border }]}
          placeholder="Titre du document"
          value={simpleData.titre}
          onChangeText={(t) => setSimpleData((p) => ({ ...p, titre: t }))}
          placeholderTextColor={theme.textSecondary}
        />

        <TextInput
          style={[styles.input, { backgroundColor: theme.background, color: theme.text, borderColor: theme.border }]}
          placeholder="Nom du document"
          value={simpleData.nomDocument}
          onChangeText={(t) => setSimpleData((p) => ({ ...p, nomDocument: t }))}
          placeholderTextColor={theme.textSecondary}
        />

        <RichTextEditor
          value={simpleData.description}
          onChange={(t) => setSimpleData((p) => ({ ...p, description: t }))}
          placeholder="Description du document"
          style={{ marginBottom: 16 }}
          theme={theme}
        />

        <TouchableOpacity
          style={[styles.documentUpload, simpleData.document && styles.documentUploaded, { borderColor: theme.border }]}
          onPress={pickPDF}
        >
          <LinearGradient colors={[theme.primary + "20", theme.primary + "10"]} style={styles.documentUploadContent}>
            <MaterialCommunityIcons name={simpleData.document ? "file-check" : "file-upload"} size={30} color={theme.primary} />
            <Text style={[styles.documentUploadText, { color: theme.text }]}>
              {simpleData.document ? simpleData.document.name : "Télécharger le document (PDF)"}
            </Text>
          </LinearGradient>
        </TouchableOpacity>
      </View>

      {/* Nouveau: Liens Clickables pour Document Simple */}
      <View style={[styles.section, { backgroundColor: theme.surface, borderColor: theme.border }]}>
        <View style={styles.sectionHeader}>
          <Text style={[styles.sectionTitle, { color: theme.text }]}>Liens Utiles</Text>
          <TouchableOpacity style={[styles.addSmallButton, { backgroundColor: theme.primary }]} onPress={() => addLien("simple")}>
            <MaterialCommunityIcons name="plus" size={20} color="#fff" />
          </TouchableOpacity>
        </View>

        {simpleData.liens.length === 0 ? (
          <TouchableOpacity style={[styles.emptyStateButton, { borderColor: theme.border }]} onPress={() => addLien("simple")}>
            <MaterialCommunityIcons name="link-plus" size={32} color={theme.textSecondary} />
            <Text style={[styles.emptyStateText, { color: theme.textSecondary }]}>Ajouter un lien utile</Text>
          </TouchableOpacity>
        ) : (
          simpleData.liens.map((lien) => (
            <View key={lien.id} style={[styles.lienContainer, { backgroundColor: theme.background, borderColor: theme.border }]}>
              <View style={styles.lienHeader}>
                <MaterialCommunityIcons name="link" size={24} color={theme.primary} />
                <Text style={[styles.lienTitle, { color: theme.text }]}>Lien</Text>
                <TouchableOpacity style={styles.deleteButton} onPress={() => removeLien("simple", lien.id)}>
                  <MaterialCommunityIcons name="delete" size={20} color={theme.danger} />
                </TouchableOpacity>
              </View>

              <TextInput
                style={[styles.input, { backgroundColor: theme.surface, color: theme.text, borderColor: theme.border }]}
                placeholder="Titre du lien"
                value={lien.titre}
                onChangeText={(t) => updateLien("simple", lien.id, "titre", t)}
                placeholderTextColor={theme.textSecondary}
              />

              <TextInput
                style={[styles.input, { backgroundColor: theme.surface, color: theme.text, borderColor: theme.border }]}
                placeholder="URL du lien"
                value={lien.url}
                onChangeText={(t) => updateLien("simple", lien.id, "url", t)}
                placeholderTextColor={theme.textSecondary}
                autoCapitalize="none"
                keyboardType="url"
              />

              <TextInput
                style={[styles.input, styles.textArea, { backgroundColor: theme.surface, color: theme.text, borderColor: theme.border }]}
                placeholder="Description du lien"
                value={lien.description}
                onChangeText={(t) => updateLien("simple", lien.id, "description", t)}
                multiline
                numberOfLines={2}
                placeholderTextColor={theme.textSecondary}
              />
            </View>
          ))
        )}
      </View>

      {/* Option de restriction de téléchargement — SUSPENDUE */}
{RESTRICTION_FEATURE_ENABLED && (
<View style={[styles.restrictionContainer, { backgroundColor: theme.surface, borderColor: theme.border, marginTop: 16 }]}>
  <View style={styles.restrictionInfo}>
    <MaterialCommunityIcons name="lock-outline" size={24} color={theme.primary} />
    <View style={styles.restrictionTextContainer}>
      <Text style={[styles.restrictionTitle, { color: theme.text }]}>
        Restriction de téléchargement
      </Text>
      <Text style={[styles.restrictionDescription, { color: theme.textSecondary }]}>
        Si activée, les utilisateurs ne pourront pas télécharger le document, seulement le consulter dans l'app
      </Text>
    </View>
  </View>
  <input
    type="checkbox"
    checked={simpleData.restrictionTelechargement}
    onChange={(e) => setSimpleData((p) => ({ ...p, restrictionTelechargement: e.target.checked }))}
    style={{ width: 48, height: 24, cursor: "pointer" }}
  />
</View>
)}

      <TouchableOpacity
        style={[styles.submitButton, (!formValid || loading) && styles.submitButtonDisabled]}
        onPress={handleSubmit}
        disabled={!formValid || loading}
      >
        <LinearGradient colors={[theme.primary, theme.primaryDark]} style={styles.submitButtonContent}>
          {loading ? <ActivityIndicator color="#fff" /> : <Text style={[styles.submitText, { color: "#fff" }]}>Publier le Document</Text>}
        </LinearGradient>
      </TouchableOpacity>
    </View>
  );

  // Sur le web (dashboard), on enveloppe dans AdminLayout (sidebar + topbar).
  // Sur mobile, on garde le SafeAreaView d'origine avec son header coloré.

  const subtitle = !selectedType
    ? 'Choisis le type de document à publier'
    : selectedType === 'concours'
    ? 'Nouveau concours / sujet d\'épreuve'
    : selectedType === 'bords'
    ? 'Nouveau bord (fiche / résumé)'
    : 'Nouveau document';

  const content = !selectedType ? (
    renderTypeSelection()
  ) : (
    <>
      {selectedType === 'concours' && renderConcoursForm()}
      {selectedType === 'bords' && renderBordsForm()}
      {selectedType === 'simple' && renderSimpleForm()}
    </>
  );

  if (isWeb) {
    return (
      <AdminLayout title="Publier un document" subtitle={subtitle}>
        {content}
      </AdminLayout>
    );
  }

  return (
    <SafeAreaView style={[styles.safeArea, { backgroundColor: theme.primary }]}>
      <StatusBar barStyle="light-content" />
      {renderHeader()}
      <ScrollView style={[styles.container, { backgroundColor: theme.background }]} contentContainerStyle={{ paddingBottom: 40 }}>
        {content}
      </ScrollView>
    </SafeAreaView>
  );
}

const styles = StyleSheet.create({
  safeArea: { flex: 1, minHeight: "100vh" },
  container: { flex: 1, minHeight: "100vh" },
  header: {
    paddingVertical: 20,
    paddingHorizontal: 16,
    borderBottomLeftRadius: 30,
    borderBottomRightRadius: 30,
    position: "sticky",
    top: 0,
    zIndex: 100,
  },
  backButton: {
    padding: 8, borderRadius: 12, width: 40, height: 40, justifyContent: "center", alignItems: "center", cursor: "pointer",
  },
  headerTitle: { fontSize: 24, fontWeight: "bold", marginTop: 16 },
  headerProgress: { marginTop: 20 },
  headerSubtitle: { marginBottom: 8 },
  progressBar: { height: 4, borderRadius: 2 },
  progress: { height: "100%", borderRadius: 2 },
  typeSelection: { padding: 20, maxWidth: 800, margin: "0 auto", width: "100%" },
  typeCard: { marginBottom: 16, borderRadius: 20, boxShadow: "0 4px 8px rgba(0,0,0,.1)", cursor: "pointer" },
  typeCardContent: { flexDirection: "row", alignItems: "center", padding: 20, borderRadius: 20 },
  typeIconContainer: { width: 60, height: 60, borderRadius: 30, justifyContent: "center", alignItems: "center" },
  typeTextContainer: { flex: 1, marginLeft: 16 },
  typeTitle: { fontSize: 18, fontWeight: "bold", marginBottom: 4 },
  typeDescription: { opacity: 0.9 },
  formContainer: { padding: 0, maxWidth: 1100, margin: "0 auto", width: "100%" },
  input: { borderRadius: 10, padding: 12, fontSize: 14, marginBottom: 14, borderWidth: 1, width: "100%" },
  textArea: { height: 120, textAlignVertical: "top", resize: "vertical" },
  section: { marginBottom: 20, borderRadius: 14, padding: 24, borderWidth: 1 },
  sectionTitle: { fontSize: 16, fontWeight: "700", marginBottom: 16 },
  sectionHeader: { flexDirection: "row", justifyContent: "space-between", alignItems: "center", marginBottom: 16 },
  addSmallButton: { width: 32, height: 32, borderRadius: 16, justifyContent: "center", alignItems: "center", cursor: "pointer" },
  arreteContainer: { borderRadius: 12, padding: 16, marginBottom: 16, borderWidth: 1 },
  arreteHeader: { flexDirection: "row", justifyContent: "space-between", alignItems: "center", marginBottom: 12 },
  arreteTitle: { fontSize: 16, fontWeight: "600" },
  deleteButton: { padding: 4, cursor: "pointer" },
  iconBtn: { paddingHorizontal: 6, paddingVertical: 4, cursor: "pointer", backgroundColor: '#f8f9fa', padding: 10, borderRadius: 10,},
  optionLabel: { fontSize: 14, fontWeight: "500", marginBottom: 8, marginTop: 4 },
  statusIndicator: { flexDirection: "row", alignItems: "center", padding: 8, borderRadius: 8, marginTop: 8 },
  statusText: { fontSize: 12, fontWeight: "500", marginLeft: 4 },
  emptyStateButton: { alignItems: "center", justifyContent: "center", padding: 32, borderRadius: 12, borderWidth: 2, borderStyle: "dashed", cursor: "pointer" },
  emptyStateText: { marginTop: 8, fontSize: 14, fontWeight: "500" },
  typeSelector: { flexDirection: "row", borderRadius: 12, padding: 4, marginBottom: 16 },
  typeOption: { flex: 1, paddingVertical: 12, alignItems: "center", borderRadius: 8, cursor: "pointer" },
  typeOptionText: { fontSize: 15, fontWeight: "600" },
  documentContainer: { borderRadius: 12, padding: 16, marginBottom: 16, borderWidth: 1 },
  documentHeader: { flexDirection: "row", alignItems: "center", marginBottom: 12 },
  documentTitle: { flex: 1, marginLeft: 12, fontSize: 14 },
  deleteDocumentButton: { padding: 8, cursor: "pointer" },
  imageUploadContainer: { height: 200, borderRadius: 20, overflow: "hidden", marginBottom: 20, position: "relative", cursor: "pointer" },
  uploadedImage: { width: "100%", height: "100%" },
  uploadOverlay: { position: "absolute", top: 0, left: 0, right: 0, bottom: 0, justifyContent: "center", alignItems: "center" },
  uploadPlaceholder: { width: "100%", height: "100%", justifyContent: "center", alignItems: "center" },
  uploadText: { marginTop: 10, fontSize: 14 },
  documentUpload: { marginTop: 8, cursor: "pointer" },
  documentUploadContent: { flexDirection: "row", alignItems: "center", padding: 16, borderRadius: 12, borderWidth: 1, borderStyle: "dashed" },
  documentUploaded: { borderStyle: "solid" },
  documentUploadText: { marginLeft: 12, fontSize: 14, flex: 1 },
  documentItem: { flexDirection: "row", alignItems: "center", padding: 12, borderRadius: 12, marginTop: 8, borderWidth: 1 },
  documentName: { flex: 1, marginLeft: 12, fontSize: 14 },
  addButton: { marginTop: 16, cursor: "pointer" },
  addButtonContent: { flexDirection: "row", alignItems: "center", justifyContent: "center", padding: 16, borderRadius: 12 },
  addButtonText: { marginLeft: 8, fontSize: 16, fontWeight: "600" },
  row: { flexDirection: "row", alignItems: "center" },
  flex1: { flex: 1 },
  ml2: { marginLeft: 8 },
  echantillonsContainer: { flexDirection: "row", overflowX: "auto", paddingBottom: 10 },
  echantillonWrapper: { marginRight: 12, position: "relative" },
  echantillonImage: { width: 120, height: 120, borderRadius: 12 },
  deleteEchantillonButton: { position: "absolute", top: 8, right: 8, borderRadius: 12, cursor: "pointer" },
  addEchantillonButton: { width: 120, height: 120, borderRadius: 12, overflow: "hidden", cursor: "pointer" },
  addEchantillonContent: { width: "100%", height: "100%", justifyContent: "center", alignItems: "center" },
  submitButton: { marginVertical: 24, cursor: "pointer", marginTop: 28 },
  submitButtonDisabled: { opacity: 0.5, cursor: "not-allowed" },
  submitButtonContent: { padding: 14, borderRadius: 10, alignItems: "center", justifyContent: "center" },
  submitText: { fontSize: 15, fontWeight: "700" },
  groupContainer: { marginBottom: 20, borderRadius: 12, borderWidth: 1, padding: 16 },
  groupHeader: { flexDirection: "row", alignItems: "center", marginBottom: 16, gap: 6 },
  groupNameInput: { fontSize: 18, fontWeight: "bold", padding: 8, borderRadius: 8, borderWidth: 1, width: "100%" },
  deleteGroupButton: { padding: 6, cursor: "pointer" },
  gratuitButton: { borderRadius: 12, padding: 16, alignItems: "center", borderWidth: 1, cursor: "pointer" },
  gratuitButtonText: { fontSize: 16, fontWeight: "600" },
  removeFeuilletage: { flexDirection: "row", alignItems: "center", padding: 8, borderRadius: 8, borderWidth: 1, alignSelf: "flex-start", cursor: "pointer" },
  removeFeuilletageText: { fontSize: 14, fontWeight: "500" },
  description: { fontSize: 14, lineHeight: 20, opacity: 0.8 },
  
  // Nouveaux styles
  formattingToolbar: {
    flexDirection: "row",
    padding: 8,
    borderRadius: 8,
    marginBottom: 8,
    borderWidth: 1,
    gap: 8,
  },
  formatButton: {
    padding: 8,
    borderRadius: 6,
    backgroundColor: "rgba(0,0,0,.05)",
    cursor: "pointer",
  },
  formattingHint: {
    fontSize: 12,
    marginTop: 4,
    fontStyle: "italic",
  },
  lienContainer: {
    borderRadius: 12,
    padding: 16,
    marginBottom: 16,
    borderWidth: 1,
  },
  lienHeader: {
    flexDirection: "row",
    alignItems: "center",
    marginBottom: 12,
  },
  lienTitle: {
    flex: 1,
    marginLeft: 12,
    fontSize: 16,
    fontWeight: "600",
  },

  restrictionContainer: {
  flexDirection: "row",
  alignItems: "center",
  justifyContent: "space-between",
  padding: 16,
  borderRadius: 12,
  borderWidth: 1,
  marginTop: 16,
},
restrictionInfo: {
  flexDirection: "row",
  alignItems: "center",
  flex: 1,
  marginRight: 12,
},
restrictionTextContainer: {
  flex: 1,
  marginLeft: 12,
},
restrictionTitle: {
  fontSize: 16,
  fontWeight: "600",
  marginBottom: 4,
},
restrictionDescription: {
  fontSize: 12,
  lineHeight: 16,
},
});
// web